/**
 * forge-level-preloader — 铸剑阁关卡数据预加载器
 *
 * V13 forge-data-completeness：将 v5 关卡数据（map.json/rules.json/ai.py/manifest.json）
 * 从静态路径加载，存入 LevelStore + registerRuntimeRules，打通 forge ID → engine 数据管。
 *
 * 调用时机：screen-12-forge.ts openLevel/exam 点击 → preloadForgeLevel → forgeGo
 */

import { getLevelStore, normalizeMap, manifestToLevelMeta, type LevelData } from "../../storage/level-store";
import { registerRuntimeMap } from "../../engine/map-utils";
import { registerRuntimeRules } from "../../engine/rules-loader";
import type { RulesConfig } from "../../engine/types";
import { FORGE_LEVEL_MAP } from "./types";
import { isLoggedIn } from "../../core/user-context";
import { getAuthToken } from "../../services/llm-service";

const LOG_PREFIX = "[forge-preloader]";
const PRELOAD_CACHE = new Map<string, Promise<void>>();

export type ForgePreloadStage = "fetch" | "normalize" | "persist";

/** 加载 v5 关卡数据包 → 存入 LevelStore + 注册运行时地图/规则。失败会移除缓存，允许用户重试。 */
export function preloadForgeLevel(
  forgeId: string,
  onStage?: (stage: ForgePreloadStage, detail: string) => void,
): Promise<void> {
  const cacheKey = `${forgeId}:${getAuthToken() ?? "anonymous"}`;
  const cached = PRELOAD_CACHE.get(cacheKey);
  if (cached) return cached;

  const task = loadForgeLevel(forgeId, onStage).catch((error) => {
    PRELOAD_CACHE.delete(cacheKey);
    throw error;
  });
  PRELOAD_CACHE.set(cacheKey, task);
  return task;
}

async function loadForgeLevel(
  forgeId: string,
  onStage?: (stage: ForgePreloadStage, detail: string) => void,
): Promise<void> {
  const v5Dir = FORGE_LEVEL_MAP[forgeId];
  if (!v5Dir) throw new Error(`${LOG_PREFIX} FORGE_LEVEL_MAP 无此 ID: ${forgeId}`);

  const fullId = `forge-${forgeId}`;
  const base = `${import.meta.env.BASE_URL}data/levels/_builtin/${v5Dir}`.replace(/\/$/, "");
  onStage?.("fetch", `读取 ${v5Dir} 的 MAP / RULES / AI`);

  // 并行加载所有所需文件，避免四个静态请求互相阻塞。
  const results = await Promise.allSettled([
    fetch(`${base}/map.json`).then(assertOk(`${base}/map.json`)).then(r => r.json()),
    fetch(`${base}/manifest.json`).then(r => r.ok ? r.json() : Promise.resolve(null)),
    fetch(`${base}/ai.py`).then(r => r.ok ? r.text() : Promise.resolve("# AI not found")),
    fetch(`${base}/rules.json`).then(assertOk(`${base}/rules.json`)).then(r => r.json()),
  ]);

  if (results[0].status === "rejected") throw results[0].reason;
  if (results[3].status === "rejected") throw results[3].reason;

  const mapRaw = (results[0] as PromiseFulfilledResult<any>).value;
  const manifestRaw = (results[1] as PromiseFulfilledResult<any>).value;
  const aiCode = (results[2] as PromiseFulfilledResult<string>).value;
  const rulesRaw = (results[3] as PromiseFulfilledResult<RulesConfig>).value;

  onStage?.("normalize", "校准地图尺寸、地形编码和出生点");
  const map = normalizeMap(mapRaw);
  const meta = manifestRaw
    ? manifestToLevelMeta(manifestRaw, false)
    : fallbackMeta(forgeId, map);

  const levelData: LevelData = {
    ...meta,
    levelId: fullId,
    map,
    playerTemplate: "",
    aiCode,
  };

  // 规则和地图先注册到当前会话，编辑器地图视图无需等待下一屏再回读。
  registerRuntimeRules(fullId, rulesRaw);
  registerRuntimeMap(fullId, map);

  // 未登录时跳过持久化；登录用户必须完成服务端同步后才进入编辑器。
  if (isLoggedIn()) {
    onStage?.("persist", "写入账号训练档案");
    await getLevelStore().save(levelData);
  }
  console.log(`${LOG_PREFIX} ✅ ${forgeId} → ${v5Dir} 预加载完成`);
}

/** 没有 manifest.json 时的回退 meta */
function fallbackMeta(forgeId: string, map: any): import("../../storage/level-store").LevelMeta {
  return {
    levelId: `forge-${forgeId}`,
    name: forgeId,
    difficulty: "easy" as const,
    mapSize: map.width ?? 25,
    aiName: "Opponent AI",
    description: "",
    isBuiltin: false,
    createdAt: new Date().toISOString(),
    thumbnailSrc: undefined,
    maxTicks: 1800,
    characterId: "tank",
    tags: [],
    version: "v5",
  };
}

/** fetch 断言：非 200 抛错 */
function assertOk(url: string): (r: Response) => Response {
  return (r) => {
    if (!r.ok) throw new Error(`${LOG_PREFIX} HTTP ${r.status} fetching ${url}`);
    return r;
  };
}
